import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageMeta from "../seo/PageMeta";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, limit, orderBy, query, where, doc, updateDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Menu from "../sections/Menu";
import Footer from "../sections/Footer";

const allowedEmails = [
  "iwona.bendig@egodziny.pl",
  "krzysztof.warda@egodziny.pl",
  "dominik.pogorzelski@egodziny.pl",
  "wiktor.spryszynski@egodziny.pl",
];

const parseDomain = (url) => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url.slice(0, 40);
  }
};

const formatSource = (session) => {
  if (session.utm_source) return session.utm_source;
  if (session.landingReferrer) return parseDomain(session.landingReferrer);
  if (session.referrer) return parseDomain(session.referrer);
  return "direct";
};

const formatUtm = (session) => {
  const parts = [session.utm_medium, session.utm_campaign].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
  }
  if (value.toDate) {
    return value.toDate().toLocaleString("pl-PL");
  }
  return "-";
};

const formatLandingPath = (path) => {
  if (!path) return "-";
  if (path === "/") return "Strona główna";
  if (path.startsWith("/blog/")) return `Blog: ${path.replace("/blog/", "")}`;
  if (path === "/blog") return "Blog (lista)";
  if (path.startsWith("/register")) return "Formularz rejestracji";
  if (path === "/cennik") return "Cennik";
  if (path === "/funkcje") return "Funkcje";
  if (path === "/o-nas") return "O nas";
  if (path === "/kontakt") return "Kontakt";
  if (path === "/privacy") return "Polityka prywatności";
  if (path === "/regulamin") return "Regulamin";
  return path;
};

const parseDeviceFromUserAgent = (ua) => {
  if (!ua) return null;
  let deviceType = "desktop";
  if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) deviceType = "mobile";
  else if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) deviceType = "tablet";

  let browser = null;
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome\/\d/i.test(ua)) browser = "Chrome";
  else if (/Firefox\/\d/i.test(ua)) browser = "Firefox";
  else if (/Safari\/\d/i.test(ua)) browser = "Safari";
  else if (/MSIE|Trident/i.test(ua)) browser = "IE";

  return { deviceType, browser };
};

const formatDevice = (session) => {
  const deviceType = session.deviceType || parseDeviceFromUserAgent(session.userAgent)?.deviceType;
  const browser = session.browser || parseDeviceFromUserAgent(session.userAgent)?.browser;
  if (!deviceType) return "-";
  const deviceLabel = deviceType === "mobile" ? "Mobile" : deviceType === "tablet" ? "Tablet" : "Desktop";
  return browser ? `${deviceLabel} / ${browser}` : deviceLabel;
};

const buildEntryPointStats = (sessions) => {
  const stats = {};
  for (const session of sessions) {
    const path = session.landingPath || "(brak danych)";
    const label = formatLandingPath(path);
    if (!stats[label]) {
      stats[label] = { count: 0, completed: 0, path };
    }
    stats[label].count += 1;
    if (session.isCompleted) stats[label].completed += 1;
  }
  return Object.entries(stats)
    .map(([label, data]) => ({ label, ...data }))
    .sort((a, b) => b.count - a.count);
};

export default function AdminPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [showFlagged, setShowFlagged] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/login/blog?redirect=/admin-panel");
        return;
      }
      if (!allowedEmails.includes(currentUser.email)) {
        alert("Brak dostępu");
        navigate("/");
        return;
      }
      setIsAuthorized(true);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const loadSessions = async () => {
      const sessionsQuery = query(
        collection(db, "registration_sessions"),
        orderBy("createdAt", sortOrder),
        limit(200)
      );
      const snapshot = await getDocs(sessionsQuery);
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      const enrichedRows = await Promise.all(
        rows.map(async (row) => {
          let lastEventTimestamp = row.lastEventTimestamp || row.lastEventAt || null;
          let isCompleted = typeof row.isCompleted === "boolean" ? row.isCompleted : null;
          let isCaptchaResolved = typeof row.isCaptchaResolved === "boolean" ? row.isCaptchaResolved : null;

          if (!lastEventTimestamp || isCompleted === null || isCaptchaResolved === null) {
            const eventsRef = collection(db, "registration_sessions", row.id, "events");
            const [latestSnapshot, completedSnapshot, captchaSnapshot] = await Promise.all([
              !lastEventTimestamp
                ? getDocs(query(eventsRef, orderBy("createdAt", "desc"), limit(1)))
                : Promise.resolve(null),
              isCompleted === null
                ? getDocs(query(eventsRef, where("type", "==", "submit_success"), limit(1)))
                : Promise.resolve(null),
              isCaptchaResolved === null
                ? getDocs(query(eventsRef, where("type", "==", "captcha_resolved"), limit(1)))
                : Promise.resolve(null)
            ]);

            if (!lastEventTimestamp && latestSnapshot && !latestSnapshot.empty) {
              const latestData = latestSnapshot.docs[0].data();
              lastEventTimestamp = latestData.timestamp || latestData.createdAt || null;
            }

            if (isCompleted === null && completedSnapshot) {
              isCompleted = !completedSnapshot.empty;
            }

            if (isCaptchaResolved === null && captchaSnapshot) {
              isCaptchaResolved = !captchaSnapshot.empty;
            }
          }

          return {
            ...row,
            lastEventTimestamp,
            isCompleted: isCompleted === null ? false : isCompleted,
            isCaptchaResolved: isCaptchaResolved === null ? false : isCaptchaResolved
          };
        })
      );

      setSessions(enrichedRows);
      setLoading(false);
    };

    if (isAuthorized) {
      loadSessions();
    }
  }, [isAuthorized, sortOrder]);

  const toggleFlag = async (sessionId, currentlyFlagged) => {
    const newValue = !currentlyFlagged;
    try {
      await updateDoc(doc(db, "registration_sessions", sessionId), { isFlagged: newValue });
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, isFlagged: newValue } : s))
      );
    } catch (error) {
      console.error("Nie udało się zmienić flagi sesji.", error);
    }
  };

  const toggleEmpty = async (sessionId, currentlyEmpty) => {
    const newValue = !currentlyEmpty;
    try {
      await updateDoc(doc(db, "registration_sessions", sessionId), { isEmpty: newValue });
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, isEmpty: newValue } : s))
      );
    } catch (error) {
      console.error("Nie udało się zmienić flagi pustego formularza.", error);
    }
  };

  const visibleSessions = sessions
    .filter((s) => showFlagged || !s.isFlagged)
    .filter((s) => showEmpty || !s.isEmpty);
  const entryPointStats = useMemo(() => buildEntryPointStats(visibleSessions), [visibleSessions]);

  return (
    <>
      <PageMeta path="/admin-panel" />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <Menu />
      <main style={{ padding: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0 }}>Wypełnienia formularzy</h1>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <button className="button-default btn" onClick={() => navigate("/admin-panel/page-views")}>
              Statystyki wyświetleń
            </button>
            <button className="button-default btn" onClick={() => navigate("/admin-panel/kalkulator")}>
              Wejścia kalkulator
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showFlagged}
                onChange={(e) => setShowFlagged(e.target.checked)}
              />
              Pokaż wejścia wewnętrzne
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showEmpty}
                onChange={(e) => setShowEmpty(e.target.checked)}
              />
              Pokaż puste formularze
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              Sortowanie:
              <select
                className="login-input"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                style={{ maxWidth: "220px" }}
              >
                <option value="desc">Od najnowszych</option>
                <option value="asc">Od najstarszych</option>
              </select>
            </label>
          </div>
        </div>
        {loading ? (
          <p>Ładowanie...</p>
        ) : (
          <>
          <div style={{ marginBottom: "1.5rem" }}>
            <button
              className="button-default btn"
              onClick={() => setShowStats((v) => !v)}
              style={{ marginBottom: "12px" }}
            >
              {showStats ? "Ukryj statystyki wejść" : "Pokaż statystyki wejść"}
            </button>
            {showStats && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                {entryPointStats.map((stat) => (
                  <div
                    key={stat.label}
                    style={{
                      background: "#f5f7fa",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      minWidth: "180px",
                      flex: "0 1 auto",
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>
                      {stat.label}
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 700, color: "#1a73e8" }}>
                      {stat.count}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {stat.completed} ukończonych ({stat.count > 0 ? Math.round((stat.completed / stat.count) * 100) : 0}%)
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>ID</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Start</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Ostatni event</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Czy captcha zatwierdzona</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Wewnętrzne</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Pusty</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Strona wejścia</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Źródło</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>UTM medium / campaign</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Referrer</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Urządzenie</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {visibleSessions.map((session) => (
                  <tr
                    key={session.id}
                    style={session.isFlagged ? { backgroundColor: "#fff8e1" } : session.isEmpty ? { backgroundColor: "#f0f0f0" } : undefined}
                  >
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontFamily: "monospace" }}>
                      {session.id}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatDateTime(session.startedAt || session.createdAt)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatDateTime(session.lastEventTimestamp || session.lastEventAt)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {session.isCaptchaResolved ? "Tak" : "Nie"}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {session.isFlagged ? "Tak" : "Nie"}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {session.isEmpty ? "Tak" : "Nie"}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={session.landingPath || ""}>
                      {formatLandingPath(session.landingPath)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatSource(session)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatUtm(session)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={session.landingReferrer || session.referrer || ""}>
                      {session.landingReferrer || session.referrer || "-"}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}
                        title={session.userAgent || ""}>
                      {formatDevice(session)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button
                        className="button-default btn"
                        onClick={() => navigate(`/admin-panel/${session.id}`)}
                      >
                        Zobacz eventy
                      </button>
                      <button
                        className="button-default btn"
                        style={session.isFlagged ? { background: "#e0e0e0", color: "#333" } : undefined}
                        onClick={() => toggleFlag(session.id, !!session.isFlagged)}
                      >
                        {session.isFlagged ? "Odflaguj" : "Oflaguj"}
                      </button>
                      <button
                        className="button-default btn"
                        style={session.isEmpty ? { background: "#d0d0d0", color: "#333" } : undefined}
                        onClick={() => toggleEmpty(session.id, !!session.isEmpty)}
                      >
                        {session.isEmpty ? "Oznacz niepusty" : "Oznacz pusty"}
                      </button>
                    </td>
                  </tr>
                ))}
                {visibleSessions.length === 0 && (
                  <tr>
                    <td colSpan={12} style={{ padding: "10px" }}>
                      Brak danych.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
